import { LinearProgress, linearProgressClasses } from '@mui/material';
import React from 'react';
import { Amount, max } from '@akropolis-web/primitives';

import { colors, styled } from 'core/styles';
import { Rating } from 'domain/types';
import { ratingColors } from 'domain/utils';
import { FormattedAmount } from 'components';

type ProgressBarChartProps<A extends Amount> = {
  rating: Rating;
  firstAmount: {
    title: string;
    amount: A;
  };
  secondAmount: {
    title: string;
    amount: A;
  };
};

export function ProgressBarChart<A extends Amount>({
  rating,
  firstAmount,
  secondAmount,
}: ProgressBarChartProps<A>) {
  const maxAmount = max(firstAmount.amount, secondAmount.amount);
  const firstProgress = Math.max(
    1,
    maxAmount.isZero() ? 0 : firstAmount.amount.mul(100).div(maxAmount).toNumber(),
  );
  const secondProgress = Math.max(
    1,
    maxAmount.isZero() ? 0 : secondAmount.amount.mul(100).div(maxAmount).toNumber(),
  );

  return (
    <Root>
      <LineProgressContainer>
        <span>
          {firstAmount.title}:{' '}
          <FormattedAmount sum={firstAmount.amount} precision={2} symbolSize="inherit" withSI />
        </span>
        <LineProgress
          variant="determinate"
          value={firstProgress}
          customColor={colors.lightGray[100]}
        />
      </LineProgressContainer>
      <LineProgressContainer>
        <span>
          {secondAmount.title}:{' '}
          <FormattedAmount sum={secondAmount.amount} precision={2} symbolSize="inherit" withSI />
        </span>
        <LineProgress
          variant="determinate"
          value={secondProgress}
          customColor={ratingColors[rating]}
        />
      </LineProgressContainer>
    </Root>
  );
}

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontSize: 10,
  color: colors.blackOpacity[40],
}));

const LineProgressContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}));

const LineProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'customColor',
})<{
  customColor: string;
}>(({ customColor }) => ({
  height: 24,
  backgroundColor: 'transparent',
  borderRadius: 2,

  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: customColor,
    borderRadius: 2,
  },
}));
